// apps/web/src/features/dashboards/china-requests/TrendChart.tsx (#1590, #2166)
// Created vs closed (+success) over the trend buckets, reacting to the selected
// period. Geometry is now owned by the shared `LineChartView` primitive
// (#2163); this feature wrapper keeps the OWN lazy trend request and its
// loading/error/empty states so the chart streams in after the headline numbers.
import type {
  ChinaRequestsDashboardTrend,
  ChinaRequestsSummaryQuery,
  ChinaRequestsTrendBucket,
} from '@magimp/shared';
import { formatNumber } from '../../../i18n/formatters.js';
import { LOCALE_META } from '../../../i18n/constants.js';
import { LineChartView, LoadingState, ErrorState } from '../../../ui/index.js';
import { useChinaRequestsTrend } from './queries.js';
import { SECTION_CARD, type AppLocale, type DashboardT } from './section.js';

const MSK_TIME_ZONE = 'Europe/Moscow';

// created/closed/success — the primitive colors each series from its `key`.
const SERIES_KEYS = ['created', 'closed', 'success'] as const;

interface TrendChartProps {
  query: ChinaRequestsSummaryQuery;
  locale: AppLocale;
  t: DashboardT;
}

export function TrendChart({ query, locale, t }: TrendChartProps) {
  const trend = useChinaRequestsTrend(query);

  return (
    <section style={{ ...SECTION_CARD, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        {t('dashboards:chinaRequests.trend.title')}
      </div>
      {trend.isPending ? (
        <LoadingState />
      ) : trend.isError ? (
        <ErrorState
          title={t('dashboards:chinaRequests.trend.error')}
          onRetry={() => void trend.refetch()}
          retryLabel={t('dashboards:chinaRequests.retry')}
        />
      ) : trend.data.points.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>
          {t('dashboards:chinaRequests.trend.empty')}
        </div>
      ) : (
        <ChartBody points={trend.data.points} bucket={trend.data.bucket} locale={locale} t={t} />
      )}
    </section>
  );
}

interface ChartBodyProps {
  points: ChinaRequestsDashboardTrend['points'];
  bucket: ChinaRequestsTrendBucket;
  locale: AppLocale;
  t: DashboardT;
}

function ChartBody({ points, bucket, locale, t }: ChartBodyProps) {
  const xLabels = points.map((p) => formatBucketLabel(p.bucketStart, bucket, locale));
  const series = SERIES_KEYS.map((key) => ({
    key,
    label: t(`dashboards:chinaRequests.trend.legend.${key}`),
    data: points.map((p, i) => ({ x: xLabels[i] as string, y: p[key] })),
  }));

  return (
    // INVARIANT: the trend testid lives on the chart wrapper so the loading/
    // error/empty branches (which never render LineChartView) stay testid-free.
    <div data-testid="china-requests-trend-chart">
      <LineChartView
        series={series}
        xAxisLabel={t('dashboards:chinaRequests.periodLabel')}
        ariaLabel={t('dashboards:chinaRequests.trend.title')}
        valueFormatter={(value) => formatNumber(value, locale)}
      />
    </div>
  );
}

// X-axis label for a bucket-start UTC instant, formatted in MSK so the label
// matches the bucket edge the backend computed. day/week → day+month;
// month → month+year. Uses Intl directly (no date library); the MSK timeZone
// keeps the label TZ-independent of the viewer's machine.
function formatBucketLabel(
  bucketStart: string,
  bucket: ChinaRequestsTrendBucket,
  locale: AppLocale,
): string {
  const opts: Intl.DateTimeFormatOptions =
    bucket === 'month'
      ? { month: 'short', year: 'numeric', timeZone: MSK_TIME_ZONE }
      : { day: 'numeric', month: 'short', timeZone: MSK_TIME_ZONE };
  return new Intl.DateTimeFormat(LOCALE_META[locale].intlLocale, opts).format(
    new Date(bucketStart),
  );
}
